import { useState } from 'react';
import { getCityDuration, getMultiCityPattern } from '../tripGuidance';

// Surfaced on the destination + dates step. Tells the user EXACTLY how
// many days to stay in the picked destination + WHY, what most people
// do, what the common mistakes are, and (for bundled multi-city
// destinations like "Lisbon + Porto") presents 2-4 proven itinerary
// patterns with per-stop nights + sample day-by-day reasoning.
//
// Built for non-travelers who need direction, not options.
export default function TripGuidanceCard({ destination, currentNights, onApplyPattern }) {
  const [openPattern, setOpenPattern] = useState(0);
  if (!destination?.city) return null;

  const cityDuration = getCityDuration(destination.city);
  const multiPattern = getMultiCityPattern(destination.city);

  if (!cityDuration && !multiPattern) return null;

  return (
    <div className="space-y-3">
      {/* Multi-city patterns (for bundled destinations) */}
      {multiPattern && (
        <div className="bg-gradient-to-br from-teal/8 to-gold/5 border border-teal/30 rounded-2xl p-4 sm:p-5 space-y-3">
          <div>
            <div className="text-[10px] font-bold text-teal uppercase tracking-wider mb-1">📋 What most people do</div>
            <h3 className="text-base font-bold text-brand-ink">{destination.city} — proven itineraries</h3>
            <p className="text-xs text-brand-ink/75 mt-1 leading-snug">{multiPattern.overview}</p>
            <div className="mt-2 text-[11px] text-brand-ink/65 bg-brand-ink/5 rounded-md p-2 leading-snug">
              <strong className="text-brand-ink/85">Transport between stops:</strong> {multiPattern.transport}
            </div>
          </div>

          {/* Pattern picker */}
          <div className="flex flex-wrap gap-1.5">
            {multiPattern.patterns.map((p, i) => (
              <button
                key={i}
                onClick={() => setOpenPattern(i)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${
                  i === openPattern
                    ? 'bg-teal text-navy'
                    : 'bg-brand-ink/5 text-brand-ink/75 hover:bg-brand-ink/10'
                }`}
              >
                {p.name} · {p.totalNights}n
              </button>
            ))}
          </div>

          {/* Selected pattern detail */}
          {multiPattern.patterns[openPattern] && (
            <PatternDetail
              pattern={multiPattern.patterns[openPattern]}
              destinationCity={destination.city}
              onApply={onApplyPattern}
              currentNights={currentNights}
            />
          )}
        </div>
      )}

      {/* Per-city duration guidance */}
      {cityDuration && (
        <div className="bg-navy-light border border-brand-ink/15 rounded-2xl p-4 sm:p-5 space-y-3">
          <div>
            <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-1">⏱️ How long to stay</div>
            <h3 className="text-base font-bold text-brand-ink">{destination.city.split(' + ')[0]} — {cityDuration.optimal} days is the sweet spot</h3>
            <div className="mt-1.5 flex items-center gap-2 text-xs">
              <DurationPill label="Min" value={`${cityDuration.minDays}n`} subtle />
              <DurationPill label="Sweet spot" value={`${cityDuration.sweetSpot[0]}-${cityDuration.sweetSpot[1]}n`} highlight />
              <DurationPill label="Max" value={`${cityDuration.maxDays}n`} subtle />
              {currentNights && (
                <span className="ml-auto text-[11px] text-brand-ink/65">
                  Currently planning <strong className="text-brand-ink/85">{currentNights}n</strong>
                </span>
              )}
            </div>
          </div>

          {/* Day-by-day reasoning */}
          {cityDuration.reasoning && (
            <div>
              <div className="text-xs font-bold text-brand-ink/85 mb-1.5">What you fit in each day · why this many</div>
              <div className="space-y-1">
                {Object.entries(cityDuration.reasoning).filter(([k]) => k !== 'whyNotMore').map(([key, text]) => {
                  const dayLabel = key.replace('day', 'Day ');
                  return (
                    <div key={key} className="flex gap-2 text-xs">
                      <span className="text-[10px] font-bold text-teal uppercase tracking-wider w-12 shrink-0 pt-0.5">{dayLabel}</span>
                      <span className="text-brand-ink/80 leading-snug">{text}</span>
                    </div>
                  );
                })}
              </div>
              {cityDuration.reasoning.whyNotMore && (
                <div className="mt-2 bg-amber-500/8 border border-amber-500/25 rounded-md p-2 text-[11px] text-brand-ink/75 leading-snug">
                  <strong className="text-amber-300">Why not more days:</strong> {cityDuration.reasoning.whyNotMore}
                </div>
              )}
            </div>
          )}

          {/* Common mistakes */}
          {cityDuration.commonMistakes && cityDuration.commonMistakes.length > 0 && (
            <div className="bg-rose-500/5 border border-rose-500/20 rounded-md p-2.5">
              <div className="text-[10px] font-bold text-rose-300 uppercase tracking-wider mb-1">⚠️ Common mistakes (avoid these)</div>
              <ul className="space-y-0.5 list-disc list-inside text-brand-ink/80 text-xs leading-snug">
                {cityDuration.commonMistakes.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DurationPill({ label, value, highlight, subtle }) {
  const cls = highlight
    ? 'bg-teal/20 text-teal border-teal/40'
    : subtle
    ? 'bg-brand-ink/5 text-brand-ink/70 border-brand-ink/15'
    : 'bg-brand-ink/10 text-brand-ink/85 border-brand-ink/20';
  return (
    <div className={`flex items-baseline gap-1 px-2 py-1 rounded-md border ${cls}`}>
      <span className="text-[10px] uppercase font-bold opacity-70">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}

function PatternDetail({ pattern, destinationCity, onApply, currentNights }) {
  const nightsBreakdown = Object.entries(pattern.nights).map(([city, n]) => `${city} ${n}n`).join(' · ');
  return (
    <div className="bg-navy-light border border-teal/25 rounded-xl p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <div className="font-bold text-sm text-teal">{pattern.name}</div>
          <div className="text-[10px] text-brand-ink/65 mt-0.5">{nightsBreakdown} · {pattern.totalNights} nights total</div>
        </div>
        {onApply && (
          <button
            onClick={() => onApply(pattern)}
            className="text-[10px] font-bold px-2 py-1 rounded-md bg-teal text-navy hover:bg-teal-dark transition"
          >
            Use this plan
          </button>
        )}
      </div>

      <div className="bg-brand-ink/5 rounded-md p-2 text-[11px] text-brand-ink/80 leading-snug">
        <strong className="text-brand-ink/95">Why this works:</strong> {pattern.why}
      </div>

      <div className="text-[11px] text-brand-ink/70 leading-snug">
        <strong className="text-brand-ink/85">Best for:</strong> {pattern.whoFor}
      </div>

      <div className="text-[11px] text-brand-ink/80 leading-snug bg-brand-ink/[0.03] border border-brand-ink/10 rounded-md p-2">
        <strong className="text-brand-ink/95">Sample itinerary:</strong> {pattern.sample}
      </div>
    </div>
  );
}
